import AuthRedirect from '@/components/AuthRedirect';
import ProtectedRoute from '@/components/ProtectedRoute';
import { AuthProvider } from '@/contexts/AuthContext';
import { SocketProvider } from '@/contexts/SocketContext';
import { ToastProvider } from '@/contexts/ToastContext';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import {
  Navigate,
  Route,
  BrowserRouter as Router,
  Routes,
} from 'react-router-dom';

import ProjectLayout from '@/layouts/ProjectLayout';
import BroadcastCreate from '@/pages/BroadcastCreate';
import BroadcastDetail from '@/pages/BroadcastDetail';
import BroadcastsList from '@/pages/BroadcastsList';
import DeveloperHub from '@/pages/DeveloperHub';
import FlowBuilder from '@/pages/FlowBuilder';
import FlowList from '@/pages/FlowList';
import ForgotPassword from '@/pages/ForgotPassword';
import Login from '@/pages/Login';
import NotFound from '@/pages/NotFound';
import Profile from '@/pages/Profile';
import ProjectContacts from '@/pages/ProjectContacts';
import ProjectConversations from '@/pages/ProjectConversations';
import ProjectDashboard from '@/pages/ProjectDashboard';
import ProjectTags from '@/pages/ProjectTags';
import Projects from '@/pages/Projects';
import Register from '@/pages/Register';
import ResetPassword from '@/pages/ResetPassword';
import TemplateCreate from '@/pages/TemplateCreate';
import TemplateDetail from '@/pages/TemplateDetail';
import TemplateEdit from '@/pages/TemplateEdit';
import TemplatesList from '@/pages/TemplatesList';
import WhatsAppConfigurations from '@/pages/WhatsAppConfigurations';
import './App.css';

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// Listen for auth events to clear React Query cache
window.addEventListener('auth-logout', () => {
  queryClient.clear();
});

// Also clear cache on login to ensure fresh data for new user
window.addEventListener('auth-login', () => {
  queryClient.clear();
});

/**
 * Main App component
 * @returns {JSX.Element} App component
 */
function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <SocketProvider>
          <ToastProvider>
            <Router>
              <div className="h-screen w-screen m-0 p-0">
                <Routes>
                  {/* Public Authentication Routes - Redirect if already authenticated */}
                  <Route
                    path="/login"
                    element={
                      <AuthRedirect>
                        <Login />
                      </AuthRedirect>
                    }
                  />
                  <Route
                    path="/register"
                    element={
                      <AuthRedirect>
                        <Register />
                      </AuthRedirect>
                    }
                  />
                  <Route
                    path="/forgot-password"
                    element={
                      <AuthRedirect>
                        <ForgotPassword />
                      </AuthRedirect>
                    }
                  />
                  <Route
                    path="/reset-password"
                    element={
                      <AuthRedirect>
                        <ResetPassword />
                      </AuthRedirect>
                    }
                  />

                  {/* Root route - Redirect to projects if authenticated */}
                  <Route
                    path="/"
                    element={
                      <ProtectedRoute>
                        <Navigate to="/projects" replace />
                      </ProtectedRoute>
                    }
                  />

                  {/* Profile route */}
                  <Route
                    path="/profile"
                    element={
                      <ProtectedRoute>
                        <Profile />
                      </ProtectedRoute>
                    }
                  />

                  {/* Project-specific routes */}
                  <Route
                    path="/projects"
                    element={
                      <ProtectedRoute>
                        <Projects />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/projects/:projectId"
                    element={
                      <ProtectedRoute>
                        <ProjectLayout />
                      </ProtectedRoute>
                    }
                  >
                    <Route path="" element={<ProjectDashboard />} />
                    <Route path="dashboard" element={<ProjectDashboard />} />
                    <Route path="contacts" element={<ProjectContacts />} />
                    <Route path="tags" element={<ProjectTags />} />
                    <Route path="broadcasts" element={<BroadcastsList />} />
                    <Route
                      path="broadcasts/create"
                      element={<BroadcastCreate />}
                    />
                    <Route
                      path="broadcasts/:broadcastId"
                      element={<BroadcastDetail />}
                    />
                    <Route
                      path="conversations"
                      element={<ProjectConversations />}
                    />
                    <Route
                      path="conversations/:conversationId"
                      element={<ProjectConversations />}
                    />
                    <Route path="templates" element={<TemplatesList />} />
                    <Route
                      path="templates/create"
                      element={<TemplateCreate />}
                    />
                    <Route
                      path="templates/:templateId"
                      element={<TemplateDetail />}
                    />
                    <Route
                      path="templates/:templateId/edit"
                      element={<TemplateEdit />}
                    />
                    <Route
                      path="whatsapp-configurations"
                      element={<WhatsAppConfigurations />}
                    />
                    <Route path="flows" element={<FlowList />} />
                    <Route path="flows/:flowId" element={<FlowBuilder />} />
                    <Route path="developer-hub" element={<DeveloperHub />} />
                  </Route>

                  {/* 404 - Catch all unmatched routes */}
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </div>
            </Router>
          </ToastProvider>
        </SocketProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
